/*
Implement a variant of String type that allow users to whitelist that a string does not contain PII.
It is useful for logging, because we don't want to accidentally log PII in the string we send to
logger, eg PII inadvertently included in free-form text input fields.
*/

import * as Sentry from "@sentry/browser";
import { alongEnv } from "./env";

export type NoPIIString = string & {
  __noPIIStringFlag: true;
};

interface NoPIIStringOption {
  allowWhitespace?: boolean;
}

function hasNoPII(
  input: string,
  options: NoPIIStringOption,
): input is NoPIIString {
  // special cases whitelisted
  const specialCases = [
    /Request failed with status code [0-9]{3}/,
    /Failed to fetch/,
    /Network Error/,
    /Request aborted/,
  ];

  for (const regex of specialCases) {
    if (regex.test(input)) {
      return true;
    }
  }

  if (!options.allowWhitespace && /\s/.test(input)) {
    // likely this is user inputed data!
    if (alongEnv() !== "production") {
      throw new Error("string validation failed: " + input);
    } else {
      Sentry.captureException(
        new Error("string failed NoPII validation: " + input),
      );
      return false;
    }
  }

  return true;
}

export function NoPIIFromString(
  input: string,
  options: NoPIIStringOption = { allowWhitespace: false },
) {
  if (hasNoPII(input, options)) {
    return input;
  }

  return "[REDACTED]" as NoPIIString;
}

function NoPIIFromArray(
  inputs: string[],
  options: NoPIIStringOption = { allowWhitespace: false },
) {
  return inputs.map((str) => NoPIIFromString(str, options));
}

type NoPIIInput = string | null | undefined | string[];
export type NoPIIType =
  | NoPIIString
  | null
  | undefined
  | NoPIIString[]
  | NoPIIString[][];

/**
 * Tag a string or an array of string with NoPII flag to allow them to be logged
 * to Segment.
 */
export default function NoPII(
  input: NoPIIInput,
  options: NoPIIStringOption = { allowWhitespace: false },
) {
  if (Array.isArray(input)) {
    return NoPIIFromArray(input);
  }

  if (typeof input === "string") {
    return NoPIIFromString(input, options);
  }

  return input;
}
